/**
 * Trackerity API client — thin fetch wrapper that:
 *   - resolves base URL from `VITE_API_URL` (default: http://localhost:8787)
 *   - targets the `/api/v1` namespace
 *   - attaches `x-tenant-id` from `VITE_TENANT_ID` (default: "demo")
 *   - surfaces non-2xx responses as typed errors
 *
 * Later agents can layer auth tokens, retries, and request-id tracing on top.
 */

const RAW_BASE = (import.meta.env.VITE_API_URL ?? "http://localhost:8787").replace(/\/+$/, "");
const API_BASE = `${RAW_BASE}/api/v1`;
const TENANT_ID = import.meta.env.VITE_TENANT_ID ?? "demo";

export class ApiError extends Error {
	readonly status: number;
	readonly body: unknown;
	constructor(status: number, message: string, body: unknown) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.body = body;
	}
}

export interface ApiRequestOptions extends Omit<RequestInit, "body" | "headers"> {
	body?: unknown;
	headers?: Record<string, string>;
	query?: Record<string, string | number | boolean | undefined | null>;
}

function buildUrl(path: string, query?: ApiRequestOptions["query"]): string {
	const normalized = path.startsWith("/") ? path : `/${path}`;
	const url = new URL(`${API_BASE}${normalized}`);
	if (query) {
		for (const [k, v] of Object.entries(query)) {
			if (v === undefined || v === null) continue;
			url.searchParams.set(k, String(v));
		}
	}
	return url.toString();
}

export async function apiRequest<T = unknown>(
	path: string,
	options: ApiRequestOptions = {},
): Promise<T> {
	const { body, headers, query, method, ...rest } = options;

	const mergedHeaders: Record<string, string> = {
		"x-tenant-id": TENANT_ID,
		accept: "application/json",
		...(body !== undefined ? { "content-type": "application/json" } : {}),
		...headers,
	};

	const response = await fetch(buildUrl(path, query), {
		...rest,
		method: method ?? (body !== undefined ? "POST" : "GET"),
		headers: mergedHeaders,
		body: body !== undefined ? JSON.stringify(body) : undefined,
	});

	const contentType = response.headers.get("content-type") ?? "";
	const payload: unknown = contentType.includes("application/json")
		? await response.json().catch(() => null)
		: await response.text().catch(() => null);

	if (!response.ok) {
		const extracted =
			payload &&
			typeof payload === "object" &&
			"error" in payload &&
			typeof (payload as { error?: unknown }).error === "string"
				? (payload as { error: string }).error
				: null;
		const message = extracted ?? `Trackerity API ${response.status} ${response.statusText}`;
		throw new ApiError(response.status, message, payload);
	}

	return payload as T;
}

export const apiClient = {
	get: <T = unknown>(path: string, options?: Omit<ApiRequestOptions, "body" | "method">) =>
		apiRequest<T>(path, { ...options, method: "GET" }),
	post: <T = unknown>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, "body" | "method">) =>
		apiRequest<T>(path, { ...options, method: "POST", body }),
	put: <T = unknown>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, "body" | "method">) =>
		apiRequest<T>(path, { ...options, method: "PUT", body }),
	patch: <T = unknown>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, "body" | "method">) =>
		apiRequest<T>(path, { ...options, method: "PATCH", body }),
	delete: <T = unknown>(path: string, options?: Omit<ApiRequestOptions, "body" | "method">) =>
		apiRequest<T>(path, { ...options, method: "DELETE" }),
};

export const apiBaseUrl = API_BASE;
export const tenantId = TENANT_ID;
